import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';

import { Icon } from 'assets/icons';
import { Button } from 'app/shared/button';
import { SimpleInput } from 'app/shared/input';
import { TextArea } from 'app/shared/input/textarea';
import GmModal from 'app/shared/modal/modal';
import { Spacer } from 'app/member/layouts/generic';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import useValidator from 'hooks/validation';
import { useWordSearchService } from 'hooks/api/agents/content/word-search';
import {
  BORDER_COLOR_PATTERN,
  DEFAULT_BORDER_COLOR,
  DEFAULT_WORD_COUNT,
  MAX_GRID_SIZE,
  MAX_WORD_COUNT,
  MIN_GRID_SIZE,
  MIN_WORD_COUNT,
  WORD_MODES,
  checkWords,
  maxWordsForGrid,
  parseWordList
} from '../config/word-search';
import styles from './generate.module.css';

const isIntegerInRange = (value, min, max) => {
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max;
};

const VALIDATION_CONFIG = {
  theme: {
    maxLength: 200,
    customValidator: (value, { mode }) => ({
      isValid: mode === WORD_MODES.user || !!value,
      message: 'Theme is required when generating words.'
    })
  },
  word_count: {
    customValidator: (value, { mode }) => ({
      isValid: mode === WORD_MODES.user || isIntegerInRange(value, MIN_WORD_COUNT, MAX_WORD_COUNT),
      message: `Word count must be between ${MIN_WORD_COUNT} and ${MAX_WORD_COUNT}.`
    })
  },
  words: {
    customValidator: (value, { mode, checked_words }) => {
      if (mode !== WORD_MODES.user) return { isValid: true };

      const { words, rejected } = checked_words;
      if (rejected.length) {
        const detail = rejected.map(({ word, reason }) => `"${word}" (${reason})`).join('; ');
        return { isValid: false, message: `Some words cannot be used: ${detail}.` };
      }
      return {
        isValid: words.length >= MIN_WORD_COUNT && words.length <= MAX_WORD_COUNT,
        message: `Enter between ${MIN_WORD_COUNT} and ${MAX_WORD_COUNT} words.`
      };
    }
  },
  grid_size: {
    customValidator: (value, { effective_word_count }) => {
      if (value === '') return { isValid: true };
      if (!isIntegerInRange(value, MIN_GRID_SIZE, MAX_GRID_SIZE)) {
        return {
          isValid: false,
          message: `Grid size must be between ${MIN_GRID_SIZE} and ${MAX_GRID_SIZE}.`
        };
      }
      return {
        isValid: effective_word_count <= maxWordsForGrid(Number(value)),
        message: `${effective_word_count} words will not fit on a ${value}×${value} grid.`
      };
    }
  },
  border_color: {
    customValidator: (value) => ({
      isValid: !value || BORDER_COLOR_PATTERN.test(value),
      message: 'Use a hex code (e.g. #1a73e8) or a CSS colour name.'
    })
  }
};

const resolveCheckedWords = (word_list, grid_size) =>
  checkWords(
    parseWordList(word_list),
    isIntegerInRange(grid_size, MIN_GRID_SIZE, MAX_GRID_SIZE) ? Number(grid_size) : MAX_GRID_SIZE
  );

const GenerateWordSearchForm = ({ closeModal = () => {}, onGenerated = () => {} }) => {
  const { generateWordSearch } = useWordSearchService();
  const { errors, form_is_valid, validateField } = useValidator(VALIDATION_CONFIG);
  const word_searches_in_store = useSelector((state) => state.word_searches);

  const last_border_color = useMemo(() => {
    const [latest] = Object.values(word_searches_in_store).sort(
      (a, b) => new Date(b.created_on) - new Date(a.created_on)
    );
    return latest?.border_color || DEFAULT_BORDER_COLOR;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form_data, setFormData] = useState({
    mode: WORD_MODES.generated,
    theme: '',
    word_count: String(DEFAULT_WORD_COUNT),
    word_list: '',
    grid_size: '',
    border_color: last_border_color
  });
  const [submitting, setSubmitting] = useState(false);

  const checked_words = useMemo(
    () => resolveCheckedWords(form_data.word_list, form_data.grid_size),
    [form_data.word_list, form_data.grid_size]
  );

  const is_user_mode = form_data.mode === WORD_MODES.user;
  const has_word_source = is_user_mode ? !!form_data.word_list.trim() : !!form_data.theme.trim();

  const handleChange = (field, value) => {
    const next_form_data = { ...form_data, [field]: value };
    const next_checked_words = resolveCheckedWords(
      next_form_data.word_list,
      next_form_data.grid_size
    );
    const context = {
      mode: next_form_data.mode,
      checked_words: next_checked_words,
      effective_word_count:
        next_form_data.mode === WORD_MODES.user
          ? next_checked_words.words.length
          : Number(next_form_data.word_count)
    };

    setFormData(next_form_data);
    validateField('theme', next_form_data.theme.trim(), context);
    validateField('word_count', next_form_data.word_count, context);
    validateField('grid_size', next_form_data.grid_size, context);
    validateField('border_color', next_form_data.border_color.trim(), context);
    if (field === 'word_list' || next_form_data.word_list || context.mode !== WORD_MODES.user) {
      validateField('words', next_form_data.word_list, context);
    }
  };

  const handleSubmit = async () => {
    if (!form_is_valid || !has_word_source || submitting) return;

    const { mode, theme, word_count, grid_size, border_color } = form_data;
    const data = {
      border_color: border_color.trim() || DEFAULT_BORDER_COLOR,
      ...(theme.trim() ? { theme: theme.trim() } : {}),
      ...(grid_size ? { grid_size: Number(grid_size) } : {}),
      ...(mode === WORD_MODES.user
        ? { words: parseWordList(form_data.word_list) }
        : { word_count: Number(word_count) })
    };

    setSubmitting(true);
    const { word_search_id } = await generateWordSearch(data);
    setSubmitting(false);

    if (!word_search_id) return;
    onGenerated(word_search_id);
    closeModal();
  };

  const is_hex_color = /^#[0-9a-f]{6}$/i.test(form_data.border_color);

  return (
    <div className={styles.form}>
      <div className={styles.modeSwitch}>
        <button
          type="button"
          className={cx(styles.modeOption, !is_user_mode && styles.modeOptionActive)}
          onClick={() => handleChange('mode', WORD_MODES.generated)}
        >
          <Icon name="ai" width={14} height={14} />
          <span>Generate words</span>
        </button>
        <button
          type="button"
          className={cx(styles.modeOption, is_user_mode && styles.modeOptionActive)}
          onClick={() => handleChange('mode', WORD_MODES.user)}
        >
          <Icon name="edit" width={14} height={14} />
          <span>Use my own words</span>
        </button>
      </div>
      <Spacer multiple={4} />

      <SimpleInput
        label={is_user_mode ? 'Theme (optional)' : 'Theme'}
        input_id="word_search_theme"
        placeholder="e.g. Ocean animals"
        value={form_data.theme}
        onInput={(value) => handleChange('theme', value)}
        error={errors.theme}
        required={!is_user_mode}
        autoFocus
      />
      <Spacer multiple={2} />

      {is_user_mode && (
        <>
          <TextArea
            label="Words"
            input_id="word_search_words"
            rows={5}
            placeholder={'One per line or comma-separated, e.g.\ndolphin, whale, sea horse'}
            value={form_data.word_list}
            onInput={(value) => handleChange('word_list', value)}
            error={errors.words}
          />
          {checked_words.words.length > 0 && (
            <div className={styles.wordPreview}>
              <span className={styles.wordPreviewCount}>
                {checked_words.words.length} / {MAX_WORD_COUNT}
              </span>
              {checked_words.words.map((word) => (
                <span key={word} className={styles.wordChip}>
                  {word}
                </span>
              ))}
            </div>
          )}
          <Spacer multiple={2} />
        </>
      )}

      <GridRow num_of_columns={2}>
        {!is_user_mode && (
          <GridColumn span={1}>
            <SimpleInput
              type="number"
              label="Number of words"
              input_id="word_search_word_count"
              value={form_data.word_count}
              min={MIN_WORD_COUNT}
              max={MAX_WORD_COUNT}
              onInput={(value) => handleChange('word_count', value)}
              error={errors.word_count}
            />
          </GridColumn>
        )}
        <GridColumn span={1}>
          <SimpleInput
            type="number"
            label="Grid size"
            input_id="word_search_grid_size"
            placeholder="Auto"
            value={form_data.grid_size}
            min={MIN_GRID_SIZE}
            max={MAX_GRID_SIZE}
            onInput={(value) => handleChange('grid_size', value)}
            error={errors.grid_size}
          />
        </GridColumn>
      </GridRow>
      <Spacer multiple={2} />

      <GridRow num_of_columns={6}>
        <GridColumn span={5}>
          <SimpleInput
            label="Border colour"
            input_id="word_search_border_color"
            placeholder={DEFAULT_BORDER_COLOR}
            value={form_data.border_color}
            onInput={(value) => handleChange('border_color', value)}
            error={errors.border_color}
          />
        </GridColumn>
        <GridColumn span={1}>
          <input
            type="color"
            className={styles.colorPicker}
            value={is_hex_color ? form_data.border_color : DEFAULT_BORDER_COLOR}
            onChange={(e) => handleChange('border_color', e.target.value)}
            title="Pick a colour"
          />
        </GridColumn>
      </GridRow>
      <Spacer multiple={2} />

      <p className={styles.hint}>
        {is_user_mode
          ? 'Every word is placed exactly as listed — spaces and punctuation are stripped.'
          : 'Leave the grid size empty to fit it to the words.'}{' '}
        Generation runs in the background — the puzzle appears in the list once it starts.
      </p>
      <Spacer multiple={4} />

      <GridRow num_of_columns={2}>
        <GridColumn />
        <GridColumn>
          <Button
            text="Generate"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!form_is_valid || !has_word_source}
          />
        </GridColumn>
      </GridRow>
    </div>
  );
};

export const GenerateWordSearchButton = ({ onGenerated = () => {} }) => {
  const [show_form, setShowForm] = useState(false);

  return (
    <>
      <button type="button" className={styles.generateBtn} onClick={() => setShowForm(true)}>
        <Icon name="ai" width={16} height={16} />
        <span>Generate word search</span>
      </button>

      <GmModal
        title="Generate word search"
        show_title
        show_modal={show_form}
        bodyClassName={styles.modal}
        onClose={() => setShowForm(false)}
      >
        {show_form ? <GenerateWordSearchForm onGenerated={onGenerated} /> : null}
      </GmModal>
    </>
  );
};
